import { Partners, UUID } from '@mesa-labs/mesa-api/dist/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
  bulkDocumentUploadInvoiceIds: UUID[];
  bulkDocumentUploadModalMode: 'bulk' | 'selective',
  bulkDocumentUploadRedirectLocation: 'bulk' | 'selective' | 'none',
  disclosureModalInvoiceId?: string;
  redirectedFromInterstitialModal: boolean;
  redirectedFromInvoiceSummaryEmail: boolean;
  redirectedFromPaymentSummaryEmail: boolean;
  redirectedFromUpcomingPaymentEmail: boolean;
  showAddBankAccountModal: boolean;
  showAddBankAccountModalPlaidCTA: boolean;
  addBankAccountModalTargetPartnerId?: Partners;
  showAddBankAccountSelectionModal: boolean;
  showAutoGetPaidNowModal: boolean;
  showAutoGetPaidNowPaymentRequestTotal?: string;
  showAutoGetPaidNowWasRedirected?: boolean;

  showPayOnApprovalByRequestModal: boolean;

  showBankAccountVerificationDocumentUploadModal: boolean;
  showBankAccountVerificationDocumentUploadModalViewEvent?: string;
  showBankAccountVerificationDocumentUploadModalConfirmEvent?: string;
  showBankAccountVerificationDocumentUploadModalCloseEvent?: string;

  showBankVerificationModal: boolean;
  showBankStatementsUploadModal: boolean;
  showBulkDocumentUploadModal: boolean;
  showBulkGetPaidNowModal: boolean;
  showBulkOnDemandModal: boolean;

  showBusinessVerificationDocumentUploadModal: boolean;
  showBusinessVerificationDocumentUploadModalViewEvent?: string;
  showBusinessVerificationDocumentUploadModalConfirmEvent?: string;
  showBusinessVerificationDocumentUploadModalCloseEvent?: string;

  showConfirmRemoveMfaModal: boolean;
  showGetPaidNowModal: boolean;
  showInputMfaCodeModal: boolean;
  showInputSmsCodeModal: boolean;
  showInvoiceDisclosureModal: boolean;
  showMesaOnDemandModal: boolean;
  showBulkDirectDebitOnDemandExceedsAvailableCreditLimitModal: boolean;

  showPaymentScheduleModal: boolean;
  showDirectDebitOnDemandPaymentScheduleModal: boolean;

  showMesaServiceAgreementModal: boolean;
  showMesaServiceAgreementModalSign: boolean;

  showPlaidModal: boolean;
  showPlaidModalTaskList: boolean;
  showPlaidModalUpgradeCopy: boolean;
  showPlaidModalManualSetupOption: boolean;
  showMesaServiceAgreementPartnerId?: Partners;
  showMesaServiceAgreementViewEvent?: string;
  showMesaServiceAgreementConfirmEvent?: string;
  showMesaServiceAgreementCloseEvent?: string;

  showDirectDebitAuthorizationModal: boolean;

  showPlaidModalDirectDebit: boolean;

  showRequestInFlightsModal: boolean;
  showSelectiveGetPaidNowModal: boolean;
  showSingleToBulkInterstitialModal: boolean;
  showUploadInvoiceDocumentModal: boolean;
  showWelcomeModal: boolean;
  uploadInvoiceDocumentModalInvoiceId?: UUID;

  showInviteNewUserModal: boolean;
  showInviteTeamMemberForBankSetupModal: boolean;

  showPlaidDemoModeModal: boolean;

  showChangePasswordModal: boolean;

  showIdleTimeoutWarningModal: boolean;

  showHowOnDemandWorksModal: boolean;
}

const initialState: UIState = {
  bulkDocumentUploadInvoiceIds: [],
  bulkDocumentUploadModalMode: 'bulk',
  bulkDocumentUploadRedirectLocation: 'bulk',
  disclosureModalInvoiceId: '',
  redirectedFromInterstitialModal: false,
  redirectedFromInvoiceSummaryEmail: false,
  redirectedFromPaymentSummaryEmail: false,
  redirectedFromUpcomingPaymentEmail: false,
  showPayOnApprovalByRequestModal: false,
  showAddBankAccountModal: false,
  showAddBankAccountModalPlaidCTA: false,
  showAddBankAccountSelectionModal: false,
  showAutoGetPaidNowModal: false,
  showAutoGetPaidNowPaymentRequestTotal: '',
  showAutoGetPaidNowWasRedirected: false,
  showBankAccountVerificationDocumentUploadModal: false,
  showBankVerificationModal: false,
  showBankStatementsUploadModal: false,
  showBulkDocumentUploadModal: false,
  showBulkGetPaidNowModal: false,
  showBulkOnDemandModal: false,
  showBulkDirectDebitOnDemandExceedsAvailableCreditLimitModal: false,

  showBusinessVerificationDocumentUploadModal: false,
  showBusinessVerificationDocumentUploadModalViewEvent: undefined,
  showBusinessVerificationDocumentUploadModalConfirmEvent: undefined,
  showBusinessVerificationDocumentUploadModalCloseEvent: undefined,

  showConfirmRemoveMfaModal: false,
  showGetPaidNowModal: false,
  showInputMfaCodeModal: false,
  showInputSmsCodeModal: false,
  showInvoiceDisclosureModal: false,
  showMesaOnDemandModal: false,

  showPaymentScheduleModal: false,
  showDirectDebitOnDemandPaymentScheduleModal: false,

  showMesaServiceAgreementModal: false,
  showMesaServiceAgreementModalSign: false,

  showPlaidModal: false,
  showPlaidModalTaskList: false,
  showPlaidModalUpgradeCopy: false,
  showPlaidModalManualSetupOption: false,
  showMesaServiceAgreementPartnerId: undefined,
  showMesaServiceAgreementViewEvent: undefined,
  showMesaServiceAgreementConfirmEvent: undefined,
  showMesaServiceAgreementCloseEvent: undefined,

  showDirectDebitAuthorizationModal: false,

  showPlaidModalDirectDebit: false,

  showRequestInFlightsModal: false,
  showSelectiveGetPaidNowModal: false,
  showSingleToBulkInterstitialModal: false,
  showUploadInvoiceDocumentModal: false,
  showWelcomeModal: false,
  uploadInvoiceDocumentModalInvoiceId: undefined,

  showInviteNewUserModal: false,
  showInviteTeamMemberForBankSetupModal: false,

  showPlaidDemoModeModal: false,

  showChangePasswordModal: false,

  showIdleTimeoutWarningModal: false,

  showHowOnDemandWorksModal: false,
};

const modalsSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    updateShowPayOnApprovalByRequestModal(state: UIState, action: PayloadAction<boolean>) {
      state.showPayOnApprovalByRequestModal = action.payload;
    },
    updateShowAddBankAccountModal(state: UIState, action: PayloadAction<{ show: boolean, showPlaidCTA?: boolean, targetPartnerId?: Partners }>) {
      state.showAddBankAccountModal = action.payload.show;
      state.showAddBankAccountModalPlaidCTA = action.payload.showPlaidCTA || false;
      state.addBankAccountModalTargetPartnerId = action.payload.targetPartnerId;
    },
    updateShowAddBankAccountSelectionModal(state: UIState, action: PayloadAction<boolean>) {
      state.showAddBankAccountSelectionModal = action.payload;
    },
    updateShowBankStatementsUploadModal(state: UIState, action: PayloadAction<boolean>) {
      state.showBankStatementsUploadModal = action.payload;
    },
    updateShowBankVerificationModal(state: UIState, action: PayloadAction<{
      show: boolean,
    }>) {
      state.showBankVerificationModal = action.payload.show;
    },
    updateShowInputMfaCodeModal(state: UIState, action: PayloadAction<boolean>) {
      state.showInputMfaCodeModal = action.payload;
    },
    updateShowInputSmsCodeModal(state: UIState, action: PayloadAction<boolean>) {
      state.showInputSmsCodeModal = action.payload;
    },
    updateShowGetPaidNowModal(state: UIState, action: PayloadAction<{ show: boolean, redirectedFromInterstitialModal?: boolean }>) {
      state.showGetPaidNowModal = action.payload.show;
      state.redirectedFromInterstitialModal = action.payload.redirectedFromInterstitialModal || false;
    },
    updateShowSingleToBulkInterstitialModal(state: UIState, action: PayloadAction<boolean>) {
      state.showSingleToBulkInterstitialModal = action.payload;
    },
    updateShowBulkGetPaidNowModal(
      state: UIState,
      action: PayloadAction<{ show: boolean, redirectedFromInterstitialModal?: boolean, redirectedFromUpcomingPaymentEmail?: boolean, redirectedFromPaymentSummaryEmail?: boolean }>,
    ) {
      state.showBulkGetPaidNowModal = action.payload.show;
      state.redirectedFromInterstitialModal = action.payload.redirectedFromInterstitialModal || false;
      state.redirectedFromInvoiceSummaryEmail = action.payload.redirectedFromUpcomingPaymentEmail || false;
      state.redirectedFromPaymentSummaryEmail = action.payload.redirectedFromPaymentSummaryEmail || false;
    },
    updateShowBulkOnDemandModal(state: UIState, action: PayloadAction<{ show: boolean, redirectedFromInterstitialModal?: boolean }>) {
      state.showBulkOnDemandModal = action.payload.show;
      state.redirectedFromInterstitialModal = action.payload.redirectedFromInterstitialModal || false;
    },
    updateShowAutoGetPaidNowModal(
      state: UIState,
      action: PayloadAction<{ show: boolean, paymentRequestTotal?: string, wasRedirected?: boolean, redirectedFromPaymentSummaryEmail?: boolean }>,
    ) {
      state.showAutoGetPaidNowModal = action.payload.show;
      state.showAutoGetPaidNowPaymentRequestTotal = action.payload.paymentRequestTotal;
      state.showAutoGetPaidNowWasRedirected = action.payload.wasRedirected;
      state.redirectedFromPaymentSummaryEmail = action.payload.redirectedFromPaymentSummaryEmail || false;
    },
    updateShowMesaOnDemandModal(state: UIState, action: PayloadAction<{ show: boolean, redirectedFromInterstitialModal?: boolean }>) {
      state.showMesaOnDemandModal = action.payload.show;
      state.redirectedFromInterstitialModal = action.payload.redirectedFromInterstitialModal || false;
    },
    updateShowConfirmRemoveMfaModal(state: UIState, action: PayloadAction<boolean>) {
      state.showConfirmRemoveMfaModal = action.payload;
    },
    updateShowBankAccountVerificationDocumentUploadModal(state: UIState, action: PayloadAction<{
      show: boolean,
      viewEvent?: string | undefined,
      confirmEvent?: string | undefined,
      closeEvent?: string | undefined,
    }>) {
      state.showBankAccountVerificationDocumentUploadModal = action.payload.show;
      state.showBankAccountVerificationDocumentUploadModalViewEvent = action.payload.viewEvent || undefined;
      state.showBankAccountVerificationDocumentUploadModalConfirmEvent = action.payload.confirmEvent || undefined;
      state.showBankAccountVerificationDocumentUploadModalCloseEvent = action.payload.closeEvent || undefined;
    },
    updateShowBusinessVerificationDocumentUploadModal(
      state: UIState,
      action: PayloadAction<{
        show: boolean,
        viewEvent?: string | undefined,
        confirmEvent?: string | undefined,
        closeEvent?: string | undefined,
      }>) {
      state.showBusinessVerificationDocumentUploadModal = action.payload.show;
      state.showBusinessVerificationDocumentUploadModalViewEvent = action.payload.viewEvent || undefined;
      state.showBusinessVerificationDocumentUploadModalConfirmEvent = action.payload.confirmEvent || undefined;
      state.showBusinessVerificationDocumentUploadModalCloseEvent = action.payload.closeEvent || undefined;
    },
    updateShowPlaidModal(state: UIState, action: PayloadAction<{ show: boolean, showManualSetupOption?: boolean, showTaskList?: boolean, showUpgradeCopy?: boolean }>) {
      state.showPlaidModal = action.payload.show;
      state.showPlaidModalManualSetupOption = action.payload.showManualSetupOption || false;
      state.showPlaidModalTaskList = action.payload.showTaskList || false;
      state.showPlaidModalUpgradeCopy = action.payload.showUpgradeCopy || false;
    },
    updateShowDirectDebitAuthorizationModal(state: UIState, action: PayloadAction<boolean>) {
      state.showDirectDebitAuthorizationModal = action.payload;
    },
    updateShowPlaidModalDirectDebit(state: UIState, action: PayloadAction<boolean>) {
      state.showPlaidModalDirectDebit = action.payload;
    },
    updateShowWelcomeModal(state: UIState, action: PayloadAction<boolean>) {
      state.showWelcomeModal = action.payload;
    },
    updateShowRequestInFlightsModal(state: UIState, action: PayloadAction<boolean>) {
      state.showRequestInFlightsModal = action.payload;
    },
    updateShowSelectiveGetPaidNowModal(state: UIState, action: PayloadAction<boolean>) {
      state.showSelectiveGetPaidNowModal = action.payload;
    },
    updateShowUploadInvoiceDocumentModal(state: UIState, action: PayloadAction<boolean>) {
      state.showUploadInvoiceDocumentModal = action.payload;
    },
    updateUploadInvoiceDocumentModalInvoiceId(state: UIState, action: PayloadAction<UUID>) {
      state.uploadInvoiceDocumentModalInvoiceId = action.payload;
    },
    updateShowBulkDocumentUploadModal(state: UIState, action: PayloadAction<{
      show: boolean;
      bulkDocumentUploadModalMode: 'bulk' | 'selective';
      bulkDocumentUploadInvoiceIds: UUID[];
      bulkDocumentUploadRedirectLocation: 'bulk' | 'selective' | 'none';
    }>) {
      state.showBulkDocumentUploadModal = action.payload.show;
      state.bulkDocumentUploadModalMode = action.payload.bulkDocumentUploadModalMode;
      state.bulkDocumentUploadInvoiceIds = action.payload.bulkDocumentUploadInvoiceIds;
      state.bulkDocumentUploadRedirectLocation = action.payload.bulkDocumentUploadRedirectLocation;
    },
    updateShowInvoiceDisclosureModal(state: UIState, action: PayloadAction<boolean>) {
      state.showInvoiceDisclosureModal = action.payload;
    },
    updateDiscolureModalInvoiceId(state: UIState, action: PayloadAction<string>) {
      state.disclosureModalInvoiceId = action.payload;
    },
    updateShowMesaServiceAgreementModal(
      state: UIState,
      action: PayloadAction<{
        show: boolean,
        sign: boolean,
        partnerId?: Partners | undefined,
        viewEvent?: string | undefined,
        confirmEvent?: string | undefined,
        closeEvent?: string | undefined,
      }>) {
      state.showMesaServiceAgreementModal = action.payload.show;
      state.showMesaServiceAgreementModalSign = action.payload.sign;
      state.showMesaServiceAgreementPartnerId = action.payload.partnerId;
      state.showMesaServiceAgreementViewEvent = action.payload.viewEvent || undefined;
      state.showMesaServiceAgreementConfirmEvent = action.payload.confirmEvent || undefined;
      state.showMesaServiceAgreementCloseEvent = action.payload.closeEvent || undefined;
    },
    updateShowPaymentScheduleModal(state: UIState, action: PayloadAction<boolean>) {
      state.showPaymentScheduleModal = action.payload;
    },
    updateShowDirectDebitOnDemandPaymentScheduleModal(state: UIState, action: PayloadAction<boolean>) {
      state.showDirectDebitOnDemandPaymentScheduleModal = action.payload;
    },
    updateShowInviteNewUserModal(state: UIState, action: PayloadAction<boolean>) {
      state.showInviteNewUserModal = action.payload;
    },
    updateShowInviteTeamMemberForBankSetupModal(state: UIState, action: PayloadAction<boolean>) {
      state.showInviteTeamMemberForBankSetupModal = action.payload;
    },
    updateShowPlaidDemoModeModal(state: UIState, action: PayloadAction<boolean>) {
      state.showPlaidDemoModeModal = action.payload;
    },
    updateShowChangePasswordModal(state: UIState, action: PayloadAction<boolean>) {
      state.showChangePasswordModal = action.payload;
    },
    updateShowIdleTimeoutWarningModal(state: UIState, action: PayloadAction<boolean>) {
      state.showIdleTimeoutWarningModal = action.payload;
    },
    updateShowBulkDirectDebitOnDemandExceedsAvailableCreditLimitModal(state: UIState, action: PayloadAction<boolean>) {
      state.showBulkDirectDebitOnDemandExceedsAvailableCreditLimitModal = action.payload;
    },
    updateShowHowOnDemandWorksModal(state: UIState, action: PayloadAction<boolean>) {
      state.showHowOnDemandWorksModal = action.payload;
    },
  },
});

export const {
  updateDiscolureModalInvoiceId,
  updateShowPayOnApprovalByRequestModal,
  updateShowAddBankAccountModal,
  updateShowAddBankAccountSelectionModal,
  updateShowAutoGetPaidNowModal,
  updateShowBankAccountVerificationDocumentUploadModal,
  updateShowBankVerificationModal,
  updateShowBankStatementsUploadModal,
  updateShowBulkDocumentUploadModal,
  updateShowBulkGetPaidNowModal,
  updateShowBulkOnDemandModal,
  updateShowBusinessVerificationDocumentUploadModal,
  updateShowConfirmRemoveMfaModal,
  updateShowPaymentScheduleModal,
  updateShowDirectDebitOnDemandPaymentScheduleModal,
  updateShowGetPaidNowModal,
  updateShowInputMfaCodeModal,
  updateShowInputSmsCodeModal,
  updateShowInvoiceDisclosureModal,
  updateShowMesaOnDemandModal,
  updateShowMesaServiceAgreementModal,
  updateShowPlaidModal,
  updateShowDirectDebitAuthorizationModal,
  updateShowRequestInFlightsModal,
  updateShowSelectiveGetPaidNowModal,
  updateShowSingleToBulkInterstitialModal,
  updateShowUploadInvoiceDocumentModal,
  updateShowWelcomeModal,
  updateUploadInvoiceDocumentModalInvoiceId,
  updateShowInviteNewUserModal,
  updateShowInviteTeamMemberForBankSetupModal,
  updateShowPlaidDemoModeModal,
  updateShowChangePasswordModal,
  updateShowIdleTimeoutWarningModal,
  updateShowPlaidModalDirectDebit,
  updateShowBulkDirectDebitOnDemandExceedsAvailableCreditLimitModal,
  updateShowHowOnDemandWorksModal,
} = modalsSlice.actions;
export default modalsSlice.reducer;
